import { useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/constants";
import { useNoPrefill } from "@/lib/useNoPrefill";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const dest = next && next.startsWith("/") ? next : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailNoPrefill = useNoPrefill();
  const passwordNoPrefill = useNoPrefill();
  const forgotNoPrefill = useNoPrefill();

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail, base_url: window.location.origin });
      setForgotSent(true);
    } catch {
      setForgotSent(true); // never reveal whether the account exists
    } finally {
      setForgotLoading(false);
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="h-6 w-6 animate-spin text-[#00FF66]" />
      </div>
    );
  }
  if (user) return <Navigate to={dest} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(dest);
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-6">
      <div className="hero-grid absolute inset-0" aria-hidden="true" />
      <div className="absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-[#00FF66]/10 blur-[130px]" aria-hidden="true" />

      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-10 text-center">
          <div className="mb-4 flex items-center justify-center gap-2.5">
            <span className="block h-3.5 w-3.5 rotate-45 rounded-[3px] bg-[#00FF66] shadow-[0_0_18px_rgba(0,255,102,0.8)]" />
            <span className="font-display text-xl font-extrabold tracking-[0.28em] text-white">VEKTOR</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white">Sign in</h1>
          <p className="mt-2 text-sm text-white/45">Welcome back. Enter your details to open your workspace.</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className={`rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl ${forgot ? "hidden" : ""}`}
          data-testid="login-form"
        >
          {error && (
            <p className="mb-5 rounded-xl border border-[#FF8B94]/30 bg-[#FF8B94]/10 px-4 py-3 text-sm text-[#FF8B94]" data-testid="login-error">
              {error}
            </p>
          )}
          <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/40">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mb-5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
            placeholder="you@company.com"
            autoComplete="username"
            {...emailNoPrefill}
            data-testid="login-email-input"
          />
          <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/40">Password</label>
          <div className="relative mb-7">
            <input
              type={showPassword ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 pr-11 text-sm text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
              placeholder="••••••••"
              autoComplete="current-password"
              {...passwordNoPrefill}
              data-testid="login-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 transition-colors duration-200 hover:text-white"
              data-testid="login-toggle-password-visibility"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <motion.button
            type="submit"
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#00FF66] py-3.5 text-sm font-bold text-black shadow-[0_0_36px_rgba(0,255,102,0.3)] disabled:opacity-60"
            data-testid="login-submit-button"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign in
          </motion.button>
          <button
            type="button"
            onClick={() => { setForgot(true); setForgotEmail(email); setForgotSent(false); }}
            className="mt-5 block w-full text-center text-xs font-semibold text-white/40 transition-colors duration-200 hover:text-[#00FF66]"
            data-testid="forgot-password-link"
          >
            Forgot your password?
          </button>
        </form>

        {forgot && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-xl" data-testid="forgot-form">
            {forgotSent ? (
              <div data-testid="forgot-sent">
                <h2 className="font-display text-lg font-bold text-white">Check your email</h2>
                <p className="mt-2 text-sm leading-relaxed text-white/50">
                  If an account exists for <span className="font-semibold text-white/70">{forgotEmail}</span>, we've sent a link to reset your password. It expires in 1 hour.
                </p>
                <button
                  type="button"
                  onClick={() => setForgot(false)}
                  className="mt-6 w-full rounded-full border border-white/15 py-3 text-sm font-bold text-white/70 transition-colors duration-200 hover:text-white"
                  data-testid="forgot-back-to-signin"
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgot}>
                <h2 className="font-display text-lg font-bold text-white">Reset your password</h2>
                <p className="mb-5 mt-1 text-sm text-white/45">Enter your email and we'll send you a reset link.</p>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.18em] text-white/40">Email</label>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="mb-6 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition-colors duration-200 focus:border-[#00FF66]/60"
                  placeholder="you@company.com"
                  autoComplete="username"
                  {...forgotNoPrefill}
                  data-testid="forgot-email-input"
                />
                <motion.button
                  type="submit"
                  disabled={forgotLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#00FF66] py-3.5 text-sm font-bold text-black disabled:opacity-60"
                  data-testid="forgot-submit-button"
                >
                  {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send reset link
                </motion.button>
                <button
                  type="button"
                  onClick={() => setForgot(false)}
                  className="mt-4 block w-full text-center text-xs font-semibold text-white/40 transition-colors duration-200 hover:text-white"
                  data-testid="forgot-cancel"
                >
                  Back to sign in
                </button>
              </form>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-sm text-white/35">
          New here?{" "}
          <Link to="/signup" className="font-semibold text-white/60 transition-colors duration-200 hover:text-[#00FF66]" data-testid="login-signup-link">
            Start a free trial
          </Link>
        </p>
        <p className="mt-3 text-center text-sm text-white/35">
          <Link to="/" className="transition-colors duration-200 hover:text-[#00FF66]" data-testid="login-back-link">
            ← Back to vektornow.com
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
